import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../utils/cn';
import styles from './Field.module.css';
import type { FieldErrorLike } from './fieldUtils';

export type InputVariant = 'text' | 'email' | 'password' | 'number' | 'tel';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  variant?: InputVariant;
  error?: FieldErrorLike | undefined;
  helper?: ReactNode;
  /** When true, adds an asterisk to the label. */
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    variant = 'text',
    error,
    helper,
    required,
    id: idProp,
    className,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? `input-${reactId}`;
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = variant === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : variant;
  const errorMsg = error?.message;
  const errorId = errorMsg ? `${id}-error` : undefined;
  const helperId = helper ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn(styles.wrapper, className)}>
      {label !== undefined ? (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div className={styles.controlWrap}>
        <input
          ref={ref}
          id={id}
          type={inputType}
          aria-invalid={errorMsg !== undefined || undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          className={cn(
            styles.control,
            errorMsg !== undefined && styles.invalid,
            isPassword && styles.controlWithToggle,
          )}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
        ) : null}
      </div>
      {errorMsg ? (
        <p id={errorId} className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : helper ? (
        <p id={helperId} className={styles.helper}>
          {helper}
        </p>
      ) : null}
    </div>
  );
});
